/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { IDropdownStyleProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { VisualizedCustomClassificationResult } from 'src/customText/appShell/testModelPage/VisualizedCustomClassificationResult';
import { CustomTextTestModelResults } from 'src/customText/core/services/testModels/types';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import { VisualizedNERResult } from 'src/languageStudio/appShell/prebuiltDemos/components/VisualizedNERResult';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';

const messages = defineMessages({
    title: { id: 'customText.customTextTestModelPage.title', defaultMessage: 'Testing deployments' },
    runTestButton: { id: 'customText.customTextTestModelPage.runTestButton', defaultMessage: 'Run the test' },
    deploymentNameLabel: { id: 'customText.customTextTestModelPage.deploymentNameLabel', defaultMessage: 'Deployment name' },
    hideCardsMessage: { id: 'customText.customTextTestModelPage.hideCardsMessage', defaultMessage: 'Turn on cards to see entities cards' },
    extractionPageSubtitle: {
        id: 'customText.customTextTestModelPage.extractionPageSubtitle',
        defaultMessage:
            'Test a deployment by providing sample text to find out what entities get recognized. These are the same predictions as when making API calls against your model in code.'
    },
    singleClassPageSubtitle: {
        id: 'customText.customTextTestModelPage.singleClassPageSubtitle',
        defaultMessage:
            'Test a deployment by providing sample text to find out what class it gets categorized as. These are the same predictions as when making API calls against your model in code.'
    },
    multiClassPageSubtitle: {
        id: 'customText.customTextTestModelPage.multiClassPageSubtitle',
        defaultMessage:
            'Test a deployment by providing sample text to find out what classes it gets categorized as. These are the same predictions as when making API calls against your model in code.'
    },
    learnMore: {
        id: 'customText.customTextTestModelPage.learnMore',
        defaultMessage: 'Learn about the info in these API requests and responses.'
    },
    deploymentNamePlaceholder: {
        id: 'customText.customTextTestModelPage.deploymentNamePlaceholder',
        defaultMessage: 'Select a deployment'
    },
    selectDocumentTitle: {
        id: 'customText.customTextTestModelPage.selectDocumentTitle',
        defaultMessage: 'Enter your own text, or upload a text document'
    },
    resultsPlaceholder: {
        id: 'customText.customTextTestModelPage.resultsPlaceholder',
        defaultMessage: 'Your test results will appear here once you enter or upload some text and press Run.'
    }
});

const pageTheme = getScopedTheme('CustomTextTestModelPage');
const commandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({
    root: { borderBottom: `1px solid ${pageTheme.topBorderColor}` }
});
const deploymentsDropdownStyles = classNamesFunction<IDropdownStyleProps, IDropdownStyles>()({
    dropdown: { minWidth: '300px' },
    root: { alignSelf: 'flex-start' }
});

export const CustomTextTestModelPage = observer(() => {
    const tracker = useProgressTracker();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { dispatcher, rootStore } = useGlobalState();
    const { isLargeOrGreater } = useResponsiveMode();

    const currentProject = rootStore.currentCustomTextProjectStore;
    const languageStoreHouse = rootStore.customTextCultureStorehouse;
    const { culture, projectKind: currentModelType, multiLingual, deploymentsStoreHouse } = currentProject;
    const isExtractionProject = currentProject.projectKind === 'CustomEntityRecognition';
    const isSingleClassProject = currentProject.projectKind === 'CustomSingleLabelClassification';

    const [fileText, setFileText] = React.useState('');
    const [testedDocument, setTestedDocument] = React.useState('');
    const [selectedLanguage, setSelectedLanguage] = React.useState(culture);
    const [selectedDeploymentName, setSelectedDeploymentName] = React.useState('');
    const [testResults, setTestResults] = React.useState<CustomTextTestModelResults>();

    React.useEffect(() => {
        if (!deploymentsStoreHouse.tracker.isCompleted) {
            dispatcher.dispatch('getCustomTextDeployments');
        }
    }, [deploymentsStoreHouse.tracker.isCompleted]);

    const onTestModel = () => {
        const selectedLanguageIsoCode = languageStoreHouse.findById(selectedLanguage).isoCode;
        logButtonClick(serviceName, 'Testing', 'RunTest', null, currentProject.id);
        dispatcher.dispatch('testCustomTextModel', {
            tracker,
            toastData: {},
            document: fileText,
            language: selectedLanguageIsoCode,
            deploymentName: selectedDeploymentName,
            setTestResults
        });
        setTestedDocument(fileText);

        const resultsStep = document.querySelector('#demoPageResultsStep');
        resultsStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const handleDocumentChange = (doc: string) => {
        setTestResults(undefined);
        setFileText(doc);
        const resultsStep = window.document.querySelector('#runTestButton');
        resultsStep?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'run',
            id: 'runTestButton',
            onClick: onTestModel,
            iconProps: { iconName: 'TestBeaker' },
            text: localize(messages.runTestButton),
            disabled: !fileText.length || !selectedDeploymentName
        }
    ];

    const subtitle = (
        <>
            {localize(
                isExtractionProject
                    ? messages.extractionPageSubtitle
                    : isSingleClassProject
                    ? messages.singleClassPageSubtitle
                    : messages.multiClassPageSubtitle
            )}
            &nbsp;
            <ExternalLink href={isExtractionProject ? 'https://aka.ms/ct-test-model' : 'https://aka.ms/ctc-test-model'}>
                {localize(messages.learnMore)}
            </ExternalLink>
        </>
    );

    return (
        <Page size="container-fluid" pageName="CustomTextTestModelPage" title={localize(messages.title)} data-id="CustomTextTestModel">
            <EditableTitle title={localize(messages.title)} subtitle={subtitle} />

            <Stack tokens={{ childrenGap: 15 }}>
                <LuisCommandBar items={commandBarItems} styles={commandBarStyles} />

                <DocumentInputStep
                    samplesList={[]}
                    document={fileText}
                    displayTitle={false}
                    documentLimit={125000}
                    displayAzureResourcePicker={false}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={setSelectedLanguage}
                    onDocumentChange={handleDocumentChange}
                    languagesList={languageStoreHouse.items}
                    disableLanguageSelection={!multiLingual}
                    featureName={isExtractionProject ? 'ner' : 'classification'}
                    textFieldProps={{ label: localize(messages.selectDocumentTitle), styles: { fieldGroup: { minHeight: '200px' } } }}
                    isTextRTL={
                        selectedLanguage
                            ? isDocumentSwaggerRtl(languageStoreHouse.findById(selectedLanguage).code as Language['code'])
                            : false
                    }
                    onRenderOptions={defaultRenderer => (
                        <Stack horizontal={isLargeOrGreater} verticalAlign="end" tokens={{ childrenGap: 10 }}>
                            {defaultRenderer()}
                            <LuisDropdown
                                selectedKey={selectedDeploymentName}
                                label={localize(messages.deploymentNameLabel)}
                                isLoading={deploymentsStoreHouse.tracker.isRunning}
                                placeholder={localize(messages.deploymentNamePlaceholder)}
                                onChange={(_, op) => setSelectedDeploymentName(op.key as string)}
                                options={deploymentsStoreHouse.items.map(d => ({ key: d.deploymentName, text: d.deploymentName }))}
                                styles={mergeStyleSets(
                                    deploymentsDropdownStyles,
                                    !isLargeOrGreater ? { dropdown: { minWidth: '160px' } } : undefined
                                )}
                            />
                        </Stack>
                    )}
                />

                <ResultsStep
                    displayTitle={false}
                    isLoading={tracker.isRunning}
                    results={testResults}
                    emptyStateMessage={localize(messages.resultsPlaceholder)}>
                    {currentModelType === 'CustomEntityRecognition' ? (
                        <VisualizedNERResult<string>
                            entitiesSectionTitle=""
                            document={testedDocument}
                            hiddenEntitiesMessage={localize(messages.hideCardsMessage)}
                            entities={testResults?.entities}
                            isLabelerRTL={
                                selectedLanguage
                                    ? isDocumentSwaggerRtl(languageStoreHouse.findById(selectedLanguage).code as Language['code'])
                                    : false
                            }
                            isLabelerWordTokenized={
                                selectedLanguage
                                    ? isDocumentSwaggerWordTokenized(languageStoreHouse.findById(selectedLanguage).code as Language['code'])
                                    : false
                            }
                        />
                    ) : (
                        <VisualizedCustomClassificationResult document={testedDocument} classes={testResults?.classes ?? []} />
                    )}
                </ResultsStep>
            </Stack>
        </Page>
    );
});
